import { createContext, useContext, useState, ReactNode, ComponentProps } from "react"
import MessageModal, { MessageModalProps } from "~/components/Admin/Reservations/MessageModal"
import PosModal, { PosModalProps } from "~/components/Reservations/PosModal"
import Modal from "~/components/ui/Modal"

type GenericModalProps = ComponentProps<typeof Modal>

type ModalConfig = {
  generic: Omit<GenericModalProps, "open"> & { children: ReactNode }
  pos: Omit<GenericModalProps, "open"> & PosModalProps
  messageModal: Omit<GenericModalProps, "open"> & MessageModalProps
}

type ModalType = keyof ModalConfig

interface ModalState {
  type: ModalType
  props: ModalConfig[ModalType]
}

type ModalContextValue = {
  openModal: <T extends ModalType>(type: T, props: ModalConfig[T]) => void
  closeModal: () => void
}

const ModalContext = createContext<ModalContextValue>({
  openModal: () => {},
  closeModal: () => {},
})

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalStack, setModalStack] = useState<ModalState[]>([])

  const openModal = <T extends ModalType>(type: T, props: ModalConfig[T]) => {
    setModalStack((prev) => [...prev, { type, props }])
  }

  const closeModal = () => {
    setModalStack((prev) => {
      const newStack = [...prev]
      const closedModal = newStack.pop()
      // Safely invoke the onClose of the top modal, if it exists
      if (closedModal?.props && typeof closedModal.props.onClose === "function") {
        closedModal.props.onClose()
      }
      return newStack
    })
  }

  const renderModals = () => {
    return modalStack.map((modal, index) => {
      const commonProps = {
        open: true,
        onClose: closeModal,
      }

      switch (modal.type) {
        case "generic": {
          const { children, ...genericProps } = modal.props as ModalConfig["generic"]
          return (
            <Modal key={index} {...genericProps} {...commonProps}>
              {children}
            </Modal>
          )
        }
        case "pos": {
          const props = modal.props as ModalConfig["pos"]
          return (
            <PosModal
              key={index}
              open={true}
              setOpen={closeModal}
              onClose={closeModal}
              onChangeIntentId={async () => {}}
              {...props}
            />
          )
        }
        case "messageModal": {
          const props = modal.props as ModalConfig["messageModal"]
          return <MessageModal key={index} open={true} onClose={closeModal} sendNotification {...props} />
        }
        default:
          return null
      }
    })
  }

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {renderModals()}
    </ModalContext.Provider>
  )
}

export const useModal = () => useContext(ModalContext)
